import { useNavigate, useSearch } from '@tanstack/react-router';

import { Text } from '../../cdl/Text/Text';
import { OverviewCustomerCompanySelect } from '../../common/form-elements/CompanySelect/OverviewCustomerCompanySelect';
import { OverviewSupplierCompanySelect } from '../../common/form-elements/CompanySelect/OverviewSupplierCompanySelect';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { Page } from '../../common/Page/Page';
import { PageGrid } from '../../common/PageGrid';
import { DebouncedSearchTextInput } from '../../common/Search/DebouncedSearchTextInput/DebouncedSearchTextInput';
import { Box } from '../../common/ui/Box';
import { OfferOverviewSortSelect } from '../../offer/OfferOverview/OfferOverviewSortSelect';
import { tabToOfferStateMap } from '../../offer/OfferOverview/tabToOfferStateMap';

import { RfqTable } from './components/RfqTable';
import { useRfqPaginationTotals } from './hooks/useRfqPaginationTotals';

export const AdminRfqOverviewPage = () => {
    useDocumentTitle(translate('offer.overviewPageTitle'));

    const search = useSearch({ from: '/_app/requests-for-quotes/' });
    const navigate = useNavigate({ from: '/requests-for-quotes' });

    const rfqPaginationTotals = useRfqPaginationTotals({
        searchQuery: search.searchQuery,
        supplierIds: search.supplierIds,
        customerIds: search.customerIds,
        tabToOfferStateMap: tabToOfferStateMap,
    });

    const onTabSelect = (tab: string) => {
        navigate({
            search: (previousSearch) => ({
                ...previousSearch,
                tab,
                page: 0,
            }),
        });
    };

    return (
        <Page>
            <PageGrid>
                <Text variant="headline" as="h1">
                    {translate('navigation.requestsForQuotes')}
                </Text>

                <Box display="flex" justifyContent="space-between" columnGap={8}>
                    <Box display="flex" columnGap={8}>
                        <Box minWidth="250px">
                            <DebouncedSearchTextInput
                                placeholder={translate('offer.searchInputPlaceholder')}
                                value={search.searchQuery ?? ''}
                                onChange={(searchQuery) => {
                                    navigate({
                                        search: (previousSearch) => ({
                                            ...previousSearch,
                                            searchQuery,
                                            page: 0,
                                        }),
                                    });
                                }}
                            />
                        </Box>

                        <OverviewCustomerCompanySelect
                            value={search.customerIds ?? []}
                            onChange={(customerIds) => {
                                navigate({
                                    search: (previousSearch) => ({
                                        ...previousSearch,
                                        customerIds,
                                        page: 0,
                                    }),
                                });
                            }}
                        />

                        <OverviewSupplierCompanySelect
                            value={search.supplierIds ?? []}
                            onChange={(supplierIds) => {
                                navigate({
                                    search: (previousSearch) => ({
                                        ...previousSearch,
                                        supplierIds,
                                        page: 0,
                                    }),
                                });
                            }}
                        />
                    </Box>

                    <Box minWidth="250px">
                        <OfferOverviewSortSelect
                            onChange={(sortValue) => {
                                navigate({
                                    search: (previousSearch) => ({
                                        ...previousSearch,
                                        sortValue,
                                        page: 0,
                                    }),
                                });
                            }}
                            value={search.sortValue}
                        />
                    </Box>
                </Box>

                <RfqTable
                    filters={{
                        supplierIds: search.supplierIds,
                        customerIds: search.customerIds,
                        searchQuery: search.searchQuery,
                        states: tabToOfferStateMap[search.tab],
                        page: search.page,
                        sortField: search.sortValue?.sortField,
                        sortDirection: search.sortValue?.sortDirection,
                    }}
                    selectedTab={search.tab}
                    rfqTotals={rfqPaginationTotals.data ?? {}}
                    onTabSelect={onTabSelect}
                    onPageChange={(page) => {
                        navigate({
                            search: (previousSearch) => ({
                                ...previousSearch,
                                page,
                            }),
                        });
                    }}
                />
            </PageGrid>
        </Page>
    );
};
